import React, { FC, useEffect, useState } from 'react'
import { Dropdown, DropdownItem, DropdownToggle, FormControl, InputGroup, Nav, NavDropdown, Navbar, NavbarCollapse } from 'react-bootstrap'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { COLLECTIONS, PATHS, PRIVACY_POLICY_URL } from '../utils/shared/constants'
import { AccountLinesToggle } from './ThreeDotsToggle'
import { useText } from '../context/TextContext'
import { useOrganization } from '../context/OrganizationContext'
import { Organization } from '../models/shared/Organization'
import { useUser } from '../context/UserContext'
import BrandName from './BrandName'
import { doc, getDoc } from 'firebase/firestore'
import { useFirestore } from 'reactfire'
import { fromFirebaseDoc } from '../utils/shared/firebase'
import { UserRole } from '../models/shared/UserInvitation'

interface IProps {
    public?: boolean
    handleLogout?: () => void
    noSearch?: boolean
    fullScreen?: boolean
}

interface CustomNavItemProps extends React.ComponentProps<typeof NavDropdown.Item> {
    icon: string;
}

const IconDropdownItem: React.FC<CustomNavItemProps> = (props) => {
    const { icon, children, ...rest } = props;
    return <DropdownItem {...rest as any}>
        <i className={`bi ${icon} me-2`} />{children}
    </DropdownItem>;
}


export const Header: FC<IProps> = (props) => {
    const texts = useText();
    const { allOrganizationIds, orgId, organization, switchOrganization, orgRole } = useOrganization();
    const { claims } = useUser();
    const [organizationDetails, setOrganizationDetails] = useState<Record<string, Organization>>({});
    const firestore = useFirestore();
    const [expanded, setExpanded] = useState(false);
    const location = useLocation();

    // Close the mobile menu whenever the route changes
    useEffect(() => {
        setExpanded(false);
    }, [location.pathname]);

    useEffect(() => {
        const fetchOrganizationDetails = async () => {
            try {
                const newOrgDetails: Record<string, Organization> = {};
                for (const candidateOrgId of allOrganizationIds) {
                    if (candidateOrgId === orgId) {
                        newOrgDetails[orgId] = organization;
                        continue;
                    }

                    const orgSnap = await getDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${candidateOrgId}`));
                    const orgData = fromFirebaseDoc<Organization>(orgSnap);
                    if (orgData) {
                        newOrgDetails[candidateOrgId] = orgData;
                    }
                }

                setOrganizationDetails(newOrgDetails);
            } catch (error) {
                console.error("Failed to fetch organization details:", error);

            }
        };

        fetchOrganizationDetails();
    }, [allOrganizationIds, firestore, orgId, organization]);

    const isWorker = orgRole === UserRole.WORKER;

    const renderItems = () => {
        if (props.public) {
            return <></>
        }
        return <>
            <Navbar.Toggle />
            <NavbarCollapse>
                <Nav className='me-auto gap-3' data-org-id={orgId} onClick={() => setExpanded(false)}>
                    <NavLink to="/">{texts.pipeline}</NavLink>
                    <NavLink to={PATHS.MAP}>{texts.map}</NavLink>
                    {!isWorker && <NavLink to={PATHS.CUSTOMERS}>{texts.customers}</NavLink>}
                    {!isWorker && <NavLink to={PATHS.ARTICLES}>{texts.articles}</NavLink>}
                    {!isWorker && <NavLink to={PATHS.EQUIPMENT}>{texts.equipment}</NavLink>}
                    {organization?.features?.hoursTracking && (<NavLink to={PATHS.TIMESHEETS}>{texts.timesheets}</NavLink>)}
                </Nav>
                <Nav className='align-items-center'>

                    {!props.noSearch && <Link to={PATHS.SEARCH} onClick={() => setExpanded(false)}>
                        <InputGroup size='sm' className='rounded-pill'>
                            <FormControl placeholder={texts.search} />
                            <InputGroup.Text>
                                <i className='bi bi-search'></i>
                            </InputGroup.Text>
                        </InputGroup>
                    </Link>}
                    <Dropdown>
                        <DropdownToggle className='text-dark h3' as={AccountLinesToggle} variant='clean'></DropdownToggle>
                        <Dropdown.Menu variant='right' onClick={() => setExpanded(false)}>
                            <IconDropdownItem as={Link} to={PATHS.USER_PREFERENCES} icon="bi-sliders">{texts.userPreferences}</IconDropdownItem>
                            {orgRole === UserRole.ADMIN && (
                                <IconDropdownItem as={Link} to={PATHS.ORGANIZATIONSETTINGS} icon="bi-gear">{texts.orgSettings}</IconDropdownItem>
                            )}
                            <IconDropdownItem as={Link} to={PATHS.INTEGRATIONS} icon="bi-plug">{texts.integrations}</IconDropdownItem>
                            <IconDropdownItem as={Link} to={PATHS.SUPPORT} icon="bi-question-circle">{texts.support}</IconDropdownItem>
                            <IconDropdownItem href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" icon="bi-shield-lock">{texts.privacyPolicy}</IconDropdownItem>
                            {claims.superAdmin && <IconDropdownItem as={Link} to={PATHS.SUPER_ADMIN} icon="bi-gear">Admin</IconDropdownItem>}
                            {allOrganizationIds && allOrganizationIds.length > 1 && (
                                <>
                                    <Dropdown.Header>{texts.organizations}</Dropdown.Header>
                                    {allOrganizationIds.map(id => (
                                        <IconDropdownItem
                                            key={id}
                                            icon={id === orgId ? "bi-check-circle-fill" : "bi-building"}
                                            onClick={() => switchOrganization(id)}
                                            active={id === orgId}
                                        >
                                            {organizationDetails[id]?.name || id}
                                        </IconDropdownItem>
                                    ))}
                                </>
                            )}
                            <Dropdown.Divider />
                            <IconDropdownItem icon="bi-box-arrow-right" onClick={props.handleLogout}>{texts.logout}</IconDropdownItem>
                        </Dropdown.Menu>
                    </Dropdown>

                </Nav>
            </NavbarCollapse>
        </>
    }

    return (
        <Navbar expand="lg" variant='light' expanded={expanded} onToggle={setExpanded}>
            <div className={props.fullScreen ? 'container-fluid' : 'container'}>
                <Link to={'/'}><Navbar.Brand className='text-primary center'>
                    <BrandName />
                </Navbar.Brand></Link>

                {renderItems()}
            </div>
        </Navbar>
    )
}